/**
 * AdminDashboard Component
 *
 * System health dashboard for admin panel showing tenant deployment statistics,
 * alerts, and recent activity.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

interface DashboardStats {
  counts: {
    total: number;
    active: number;
    deploying: number;
    pendingValidation: number;
    failed: number;
    notStarted: number;
  };
  successRate: number;
  alerts: Array<{
    level: string;
    message: string;
    count: number;
  }>;
  lastUpdated: string;
}

interface Activity {
  type: string;
  tenantId: string;
  domain: string | null;
  status: string;
  timestamp: string | null;
}

const STATUS_CARDS = [
  { key: 'active', label: 'Active', color: 'green', icon: '✓' },
  { key: 'deploying', label: 'Deploying', color: 'blue', icon: '⟳' },
  { key: 'pendingValidation', label: 'Pending', color: 'yellow', icon: '⏳' },
  { key: 'failed', label: 'Failed', color: 'red', icon: '✗' },
];

export const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDashboard = useCallback(async () => {
    setError(null);

    try {
      const [statsResponse, activityResponse] = await Promise.all([
        api.get<{ data: DashboardStats }>('/admin/dashboard/stats'),
        api.get<{ data: { activities: Activity[] } }>('/admin/activity?limit=10'),
      ]);

      setStats(statsResponse.data.data);
      setActivities(activityResponse.data.data.activities);
    } catch {
      setError('Failed to load dashboard. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
    // Refresh every 30 seconds
    const interval = setInterval(loadDashboard, 30000);
    return () => clearInterval(interval);
  }, [loadDashboard]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getColorClasses = (color: string) => {
    const colors: Record<string, { bg: string; text: string; border: string }> = {
      green: {
        bg: 'bg-green-50',
        text: 'text-green-700',
        border: 'border-green-200',
      },
      blue: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
      yellow: {
        bg: 'bg-yellow-50',
        text: 'text-yellow-700',
        border: 'border-yellow-200',
      },
      red: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
    };
    return colors[color] || colors.blue;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-gray-500 mt-1">
              System health and tenant deployment overview
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={loadDashboard}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Refresh
            </button>
            <button
              onClick={() => navigate('/admin/tenants')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              View All Tenants
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Alerts */}
        {stats?.alerts && stats.alerts.length > 0 && (
          <div className="mb-6 space-y-3">
            {stats.alerts.map((alert, index) => (
              <div
                key={index}
                className={`p-4 rounded-lg flex items-center justify-between ${
                  alert.level === 'error'
                    ? 'bg-red-50 border border-red-200 text-red-700'
                    : 'bg-yellow-50 border border-yellow-200 text-yellow-700'
                }`}
              >
                <div className="flex items-center">
                  <span className="text-xl mr-3">
                    {alert.level === 'error' ? '🚨' : '⚠️'}
                  </span>
                  <span>{alert.message}</span>
                </div>
                <button
                  onClick={() =>
                    navigate(
                      `/admin/tenants?status=${
                        alert.level === 'error' ? 'FAILED' : 'PENDING_VALIDATION'
                      }`
                    )
                  }
                  className="text-sm font-medium hover:underline"
                >
                  View Details →
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {STATUS_CARDS.map((card) => {
              const colors = getColorClasses(card.color);
              const count =
                stats.counts[card.key as keyof typeof stats.counts] || 0;

              return (
                <div
                  key={card.key}
                  onClick={() =>
                    navigate(
                      `/admin/tenants?status=${card.key
                        .replace(/([A-Z])/g, '_$1')
                        .toUpperCase()}`
                    )
                  }
                  className={`${colors.bg} border ${colors.border} rounded-lg p-6 cursor-pointer hover:shadow-md transition-shadow`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`text-3xl ${colors.text}`}>{card.icon}</span>
                    <span className={`text-4xl font-bold ${colors.text}`}>
                      {count}
                    </span>
                  </div>
                  <p className={`mt-2 ${colors.text} font-medium`}>{card.label}</p>
                </div>
              );
            })}
          </div>
        )}

        {/* Summary Row */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-gray-500 uppercase">
                Total Tenants
              </h3>
              <p className="text-4xl font-bold text-gray-900 mt-2">
                {stats.counts.total}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-gray-500 uppercase">
                Success Rate
              </h3>
              <p className="text-4xl font-bold text-green-600 mt-2">
                {stats.successRate}%
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-gray-500 uppercase">
                Last Updated
              </h3>
              <p className="text-xl font-medium text-gray-700 mt-2">
                {formatDate(stats.lastUpdated)}
              </p>
            </div>
          </div>
        )}

        {/* Recent Activity */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b flex justify-between items-center">
            <h2 className="text-lg font-semibold">Recent Activity</h2>
            <button
              onClick={() => navigate('/admin/tenants')}
              className="text-blue-600 hover:text-blue-800 text-sm"
            >
              View All →
            </button>
          </div>
          <div className="divide-y">
            {activities.length === 0 ? (
              <div className="px-6 py-12 text-center text-gray-500">
                No recent activity
              </div>
            ) : (
              activities.map((activity, index) => (
                <div
                  key={index}
                  onClick={() => navigate(`/admin/tenants/${activity.tenantId}`)}
                  className="px-6 py-4 hover:bg-gray-50 cursor-pointer flex items-center justify-between"
                >
                  <div>
                    <p className="font-medium text-gray-900">
                      {activity.domain || activity.tenantId}
                    </p>
                    <p className="text-sm text-gray-500">
                      {activity.status.replace(/_/g, ' ')}
                    </p>
                  </div>
                  <span className="text-sm text-gray-400">
                    {formatDate(activity.timestamp)}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
